import { PERMISSIONS as P, SCOPES } from '@/constants/permissions'

/**
 * Definición de rutas.
 *
 * Cada ruta declara en `meta`:
 *  - `permission`: el permiso mínimo. La guarda lo comprueba contra lo que
 *    devuelve `/auth/me`; el menú se construye con el mismo dato, así que
 *    NO existe una lista de menú aparte que pueda quedar desincronizada.
 *  - `scope`: `organization` o `platform`.
 *  - `nav`: si está presente, la ruta aparece en el menú lateral.
 *
 * Todas las vistas se cargan con `import()` para que el primer arranque no
 * traiga la consola de plataforma a un cliente que jamás la verá.
 */

const AuthLayout = () => import('@/layouts/AuthLayout.vue')
const ConsoleLayout = () => import('@/layouts/ConsoleLayout.vue')

export const routes = [
  {
    path: '/',
    name: 'root',
    redirect: () => ({ name: 'app-home' }),
  },

  /* ─── Acceso ────────────────────────────────────────────────────────────── */
  {
    path: '/',
    component: AuthLayout,
    meta: { public: true },
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/pages/auth/LoginPage.vue'),
        meta: { public: true },
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
        meta: { public: true },
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/pages/auth/ResetPasswordPage.vue'),
        meta: { public: true },
      },
    ],
  },

  /* ─── Superficie del cliente ────────────────────────────────────────────── */
  {
    path: '/app',
    component: ConsoleLayout,
    meta: { scope: SCOPES.ORGANIZATION },
    children: [
      {
        path: '',
        name: 'app-home',
        component: () => import('@/pages/app/HomePage.vue'),
        meta: {
          permission: P.METRICS_READ,
          nav: { key: 'nav.home', icon: 'LayoutDashboard', order: 10 },
        },
      },
      {
        path: 'channels',
        name: 'app-channels',
        component: () => import('@/pages/app/ChannelsPage.vue'),
        meta: {
          permission: P.CHANNELS_READ,
          nav: { key: 'nav.channels', icon: 'Radio', order: 20 },
        },
      },
      {
        path: 'conversations/:conversationId?',
        name: 'app-conversations',
        component: () => import('@/pages/app/ConversationsPage.vue'),
        props: true,
        meta: {
          permission: P.CONVERSATIONS_READ,
          nav: { key: 'nav.conversations', icon: 'MessagesSquare', order: 30 },
        },
      },
      {
        path: 'agent',
        name: 'app-agent',
        component: () => import('@/pages/app/AgentPage.vue'),
        meta: {
          permission: P.BOTS_READ,
          nav: { key: 'nav.agent', icon: 'Bot', order: 40 },
        },
      },
      {
        path: 'data',
        name: 'app-data',
        component: () => import('@/pages/app/DataPage.vue'),
        meta: {
          permission: P.DATA_READ,
          nav: { key: 'nav.data', icon: 'Database', order: 50 },
        },
      },
      {
        path: 'metrics',
        name: 'app-metrics',
        component: () => import('@/pages/app/MetricsPage.vue'),
        meta: {
          permission: P.METRICS_READ,
          nav: { key: 'nav.metrics', icon: 'ChartNoAxesColumn', order: 60 },
        },
      },
      {
        path: 'backups',
        name: 'app-backups',
        component: () => import('@/pages/app/BackupsPage.vue'),
        meta: {
          permission: P.EXPORTS_READ,
          nav: { key: 'nav.backups', icon: 'DatabaseBackup', order: 70 },
        },
      },
      {
        path: 'organization',
        name: 'app-organization',
        component: () => import('@/pages/app/OrganizationPage.vue'),
        meta: {
          permission: P.ORGANIZATION_READ,
          nav: { key: 'nav.organization', icon: 'Building2', order: 80 },
        },
      },
    ],
  },

  /* ─── Consola de plataforma ─────────────────────────────────────────────── */
  {
    path: '/platform',
    component: ConsoleLayout,
    meta: { scope: SCOPES.PLATFORM },
    children: [
      {
        path: '',
        name: 'platform-clients',
        component: () => import('@/pages/platform/ClientsPage.vue'),
        meta: {
          permission: P.PLATFORM_ORGANIZATIONS_READ,
          nav: { key: 'nav.clients', icon: 'Users', order: 10 },
        },
      },
      {
        path: 'clients/:organizationId',
        name: 'platform-client-detail',
        component: () => import('@/pages/platform/ClientDetailPage.vue'),
        props: true,
        meta: { permission: P.PLATFORM_ORGANIZATIONS_READ },
      },
      {
        path: 'prompts',
        name: 'platform-prompts',
        component: () => import('@/pages/platform/PromptsPage.vue'),
        meta: {
          permission: P.PLATFORM_PROMPTS_READ,
          nav: { key: 'nav.prompts', icon: 'ScrollText', order: 20 },
        },
      },
      {
        path: 'models',
        name: 'platform-models',
        component: () => import('@/pages/platform/ModelsPage.vue'),
        meta: {
          permission: P.PLATFORM_MODELS_READ,
          nav: { key: 'nav.models', icon: 'Cpu', order: 30 },
        },
      },
      {
        path: 'plans',
        name: 'platform-plans',
        component: () => import('@/pages/platform/PlansPage.vue'),
        meta: {
          permission: P.PLATFORM_PLANS_READ,
          nav: { key: 'nav.plans', icon: 'Layers', order: 40 },
        },
      },
      {
        path: 'usage',
        name: 'platform-usage',
        component: () => import('@/pages/platform/UsagePage.vue'),
        meta: {
          permission: P.PLATFORM_USAGE_READ,
          nav: { key: 'nav.usage', icon: 'Coins', order: 50 },
        },
      },
      {
        path: 'operations',
        name: 'platform-operations',
        component: () => import('@/pages/platform/OperationsPage.vue'),
        meta: {
          permission: P.PLATFORM_OPERATIONS_READ,
          nav: { key: 'nav.operations', icon: 'Activity', order: 60 },
        },
      },
      {
        /**
         * Sección propia y no una quinta pestaña dentro de Operación: a esta
         * pantalla se entra cuando un cliente llama diciendo que no le ha
         * llegado el correo, y en ese momento se busca en el menú algo que diga
         * «correos». Una pestaña sólo la encuentra quien ya sabe que está ahí.
         */
        path: 'notifications',
        name: 'platform-notifications',
        component: () => import('@/pages/platform/NotificationsPage.vue'),
        meta: {
          permission: P.PLATFORM_OPERATIONS_READ,
          nav: { key: 'nav.notifications', icon: 'Mail', order: 65 },
        },
      },
    ],
  },

  /* ─── Errores ───────────────────────────────────────────────────────────── */
  {
    path: '/no-access',
    name: 'forbidden',
    component: () => import('@/pages/ForbiddenPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { public: true },
  },
]
